import { ChevronDown, ChevronRight, Download, Filter, RefreshCw, ShieldCheck } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { auditLogService } from '../services/api';
import './AuditLog.css';

/* ─────────────────────────────────────────────────────────────────
   Action badge configuration
───────────────────────────────────────────────────────────────── */
const ACTION_META = {
    INVOICE_GENERATED: { label: 'Fattura generata', cls: 'badge-success' },
    INVOICE_DELETED: { label: 'Fattura eliminata', cls: 'badge-danger' },
    INVOICE_DELETE_BLOCKED: { label: 'Delete bloccata', cls: 'badge-warning' },
    INVOICE_JOB_TRIGGERED: { label: 'Job mensile avviato', cls: 'badge-info' },
    INVOICE_RETRY_TRIGGERED: { label: 'Retry avviato', cls: 'badge-info' },
    CREDIT_NOTE_CREATED: { label: 'Nota di credito creata', cls: 'badge-purple' },
    CREDIT_NOTE_SENT_ACUBE: { label: 'Nota di credito inviata', cls: 'badge-success' },
};

const ENTITY_TYPE_OPTIONS = [
    'WorkerInvoice',
    'WorkerCreditNote',
    'InvoiceRun',
];

const LIMIT = 50;

/* ─────────────────────────────────────────────────────────────────
   Helpers
───────────────────────────────────────────────────────────────── */
function ActionBadge({ action }) {
    const meta = ACTION_META[action] || { label: action, cls: 'badge-default' };
    return <span className={`al-badge ${meta.cls}`}>{meta.label}</span>;
}

function formatDate(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString('it-IT', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
}

function MetadataCell({ metadata }) {
    const [open, setOpen] = useState(false);
    if (!metadata || Object.keys(metadata).length === 0) return <span className="al-meta-empty">—</span>;
    return (
        <div className="al-meta-wrap">
            <button className="al-meta-toggle" onClick={() => setOpen((v) => !v)}>
                {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                {open ? 'Nascondi' : 'Mostra'}
            </button>
            {open && (
                <pre className="al-meta-json">{JSON.stringify(metadata, null, 2)}</pre>
            )}
        </div>
    );
}

/* ─────────────────────────────────────────────────────────────────
   Main component
───────────────────────────────────────────────────────────────── */
export default function AuditLog() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [page, setPage] = useState(1);
    const [actionOptions, setActionOptions] = useState([]);

    const [filters, setFilters] = useState({
        action: '',
        entityType: '',
        adminId: '',
        q: '',
        from: '',
        to: '',
    });
    const [pendingFilters, setPendingFilters] = useState({ ...filters });

    /* ── Load action options on mount ─────────────────────────── */
    useEffect(() => {
        auditLogService.getActions()
            .then((data) => setActionOptions(data.actions || []))
            .catch(() => setActionOptions(Object.keys(ACTION_META)));
    }, []);

    /* ── Load logs ─────────────────────────────────────────────── */
    const loadLogs = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await auditLogService.getLogs({ ...filters, page, limit: LIMIT });
            setLogs(data.logs || []);
            setTotal(data.total || 0);
            setTotalPages(data.totalPages || 1);
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    }, [filters, page]);

    useEffect(() => {
        loadLogs();
    }, [loadLogs]);

    /* ── Handlers ──────────────────────────────────────────────── */
    function applyFilters() {
        setFilters({ ...pendingFilters });
        setPage(1);
    }

    function resetFilters() {
        const empty = { action: '', entityType: '', adminId: '', q: '', from: '', to: '' };
        setPendingFilters(empty);
        setFilters(empty);
        setPage(1);
    }

    function downloadCsv() {
        const headers = ['Data/Ora', 'Azione', 'Tipo entità', 'ID entità', 'Admin ID', 'IP', 'Metadata'];
        const rows = logs.map((l) => [
            formatDate(l.createdAt),
            l.action,
            l.entityType || '',
            l.entityId || '',
            l.adminId || '',
            l.ip || '',
            l.metadata ? JSON.stringify(l.metadata) : '',
        ]);
        const csv = [headers, ...rows]
            .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
            .join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    }

    /* ── Render ─────────────────────────────────────────────────── */
    return (
        <div className="al-page">
            {/* Header */}
            <div className="al-header">
                <div className="al-header-left">
                    <ShieldCheck size={22} className="al-header-icon" />
                    <h1>Audit Log</h1>
                </div>
                <div className="al-header-actions">
                    <button className="al-btn al-btn-ghost" onClick={loadLogs} disabled={loading}>
                        <RefreshCw size={15} className={loading ? 'al-spin' : ''} />
                        Aggiorna
                    </button>
                    <button className="al-btn al-btn-ghost" onClick={downloadCsv} disabled={!logs.length}>
                        <Download size={15} />
                        CSV
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="al-filters-card">
                <div className="al-filters-title">
                    <Filter size={15} />
                    Filtri
                </div>
                <div className="al-filters-grid">
                    <div className="al-field">
                        <label>Azione</label>
                        <select
                            value={pendingFilters.action}
                            onChange={(e) => setPendingFilters((f) => ({ ...f, action: e.target.value }))}
                        >
                            <option value="">Tutte</option>
                            {actionOptions.map((a) => (
                                <option key={a} value={a}>{ACTION_META[a]?.label || a}</option>
                            ))}
                        </select>
                    </div>

                    <div className="al-field">
                        <label>Tipo entità</label>
                        <select
                            value={pendingFilters.entityType}
                            onChange={(e) => setPendingFilters((f) => ({ ...f, entityType: e.target.value }))}
                        >
                            <option value="">Tutti</option>
                            {ENTITY_TYPE_OPTIONS.map((t) => (
                                <option key={t} value={t}>{t}</option>
                            ))}
                        </select>
                    </div>

                    <div className="al-field">
                        <label>ID entità</label>
                        <input
                            type="text"
                            placeholder="cerca per ID"
                            value={pendingFilters.q}
                            onChange={(e) => setPendingFilters((f) => ({ ...f, q: e.target.value }))}
                        />
                    </div>

                    <div className="al-field">
                        <label>Admin ID</label>
                        <input
                            type="text"
                            placeholder="UUID admin"
                            value={pendingFilters.adminId}
                            onChange={(e) => setPendingFilters((f) => ({ ...f, adminId: e.target.value }))}
                        />
                    </div>

                    <div className="al-field">
                        <label>Dal</label>
                        <input
                            type="date"
                            value={pendingFilters.from}
                            onChange={(e) => setPendingFilters((f) => ({ ...f, from: e.target.value }))}
                        />
                    </div>

                    <div className="al-field">
                        <label>Al</label>
                        <input
                            type="date"
                            value={pendingFilters.to}
                            onChange={(e) => setPendingFilters((f) => ({ ...f, to: e.target.value }))}
                        />
                    </div>
                </div>

                <div className="al-filters-actions">
                    <button className="al-btn al-btn-primary" onClick={applyFilters}>
                        Applica filtri
                    </button>
                    <button className="al-btn al-btn-ghost" onClick={resetFilters}>
                        Reset
                    </button>
                </div>
            </div>

            {/* Results summary */}
            {!loading && !error && (
                <div className="al-summary">
                    {total === 0
                        ? 'Nessun risultato trovato'
                        : `${total} evento${total !== 1 ? 'i' : ''} — pagina ${page} di ${totalPages}`}
                </div>
            )}

            {/* Error */}
            {error && (
                <div className="al-error">
                    Errore nel caricamento: {error}
                </div>
            )}

            {/* Table */}
            <div className="al-table-wrap">
                <table className="al-table">
                    <thead>
                        <tr>
                            <th>Data / Ora</th>
                            <th>Azione</th>
                            <th>Tipo entità</th>
                            <th>ID entità</th>
                            <th>Admin</th>
                            <th>IP</th>
                            <th>Metadata</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={7} className="al-loading-cell">
                                    <RefreshCw size={18} className="al-spin" /> Caricamento…
                                </td>
                            </tr>
                        )}
                        {!loading && logs.length === 0 && (
                            <tr>
                                <td colSpan={7} className="al-empty-cell">Nessun evento trovato</td>
                            </tr>
                        )}
                        {!loading && logs.map((log) => (
                            <tr key={log.id}>
                                <td className="al-cell-date">{formatDate(log.createdAt)}</td>
                                <td><ActionBadge action={log.action} /></td>
                                <td className="al-cell-mono">{log.entityType || '—'}</td>
                                <td className="al-cell-mono al-cell-id" title={log.entityId}>{log.entityId || '—'}</td>
                                <td className="al-cell-mono al-cell-id" title={log.adminId}>{log.adminId || '—'}</td>
                                <td className="al-cell-mono">{log.ip || '—'}</td>
                                <td><MetadataCell metadata={log.metadata} /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="al-pagination">
                    <button
                        className="al-btn al-btn-ghost al-page-btn"
                        disabled={page <= 1}
                        onClick={() => setPage((p) => p - 1)}
                    >
                        ‹ Precedente
                    </button>
                    <span className="al-page-info">Pagina {page} / {totalPages}</span>
                    <button
                        className="al-btn al-btn-ghost al-page-btn"
                        disabled={page >= totalPages}
                        onClick={() => setPage((p) => p + 1)}
                    >
                        Successiva ›
                    </button>
                </div>
            )}
        </div>
    );
}
